import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  FluentProvider,
} from '@fluentui/react-components';
import React, { useContext } from 'react';
import { BitButton } from '../BitButton';
import { useTranslation } from 'react-i18next';
import { callFunction } from '../../lib/Helpers';
import { TeamsFxContext } from '../../Context';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { toast } from 'react-toastify';
import { monetization } from '@microsoft/teams-js';

interface LicencePopupProps {
  type: 'TRIAL' | 'HAS_TRIED';
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  tid: string;
}

export const LicencePopup = ({
  type,
  isVisible,
  setIsVisible,
  tid,
}: LicencePopupProps) => {
  const appInsights = useAppInsightsContext();

  const { t } = useTranslation('licencePopups');
  const { teamsUserCredential } = useContext(TeamsFxContext);

  const initiateTrialExperience = () => {
    callFunction(
      true,
      'putTrialSubForUser?{0}&tenantId=' + tid,
      undefined,
      false,
      teamsUserCredential
    )
      .then(() => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackEvent({
            name: 'Trial initiated from popup clicked',
          });
        window.location.reload();
      })
      .catch(() => {
        toast.error(t('TRIAL_INITIATE_ERROR', { ns: 'alerts' }));
      });
  };

  const openPurchaseExperience = () => {
    if (process.env.REACT_APP_APP_INSIGHTS !== '') {
      appInsights.trackEvent({ name: 'Upgrade from popup clicked' });
      if (monetization.isSupported()) {
        monetization
          .openPurchaseExperience()
          .catch(() =>
            window.open(
              encodeURI(
                'https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'
              )
            )
          );
      } else {
        window.open(
          encodeURI(
            'https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'
          )
        );
      }
    }
  };

  return (
    <FluentProvider>
      <Dialog
        open={isVisible}
        onOpenChange={(_, data) => setIsVisible(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle style={{ textAlign: 'center' }}>
              {t(`${type}_POPUP_TITLE`)}
            </DialogTitle>
          </DialogBody>
          <DialogContent style={{ textAlign: 'center' }}>
            {t(`${type}_POPUP_CONTENT`)}
          </DialogContent>
          <DialogActions
            style={{
              marginTop: '1.2rem',
              justifyContent: 'center',
            }}
          >
            <BitButton
              label={t(`${type}_POPUP_BUTTON`)}
              action={() =>
                type === 'TRIAL'
                  ? initiateTrialExperience()
                  : openPurchaseExperience()
              }
            />
            <DialogTrigger disableButtonEnhancement>
              <BitButton
                label={t('CLOSE_POPUP_BUTTON')}
                variant="secondary"
                action={() => setIsVisible(false)}
              />
            </DialogTrigger>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </FluentProvider>
  );
};
